import { Component, Input, OnInit, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { CountriesListModel } from 'src/app/models/countries-list.model';
import { InsertBillingAddressModel } from 'src/app/models/insert-billing-address.model';
import { CommonService } from 'src/app/services/Common-function/common.service';
import { FormsService } from 'src/app/services/Forms/forms.service';
import { BillingAddressService } from 'src/app/services/billing-address/billing-address.service';
import { ShakeAnimationService } from 'src/app/services/shake-animation/shake-animation.service';
import { Ga4trackingService } from 'src/app/services/ga4Tracking/ga4tracking.service';
import { BrowserStorageService } from 'src/app/services/browser-storage/browser-storage.service';

@Component({
  selector: 'ipm-add-billing-address-form',
  templateUrl: './add-billing-address-form.component.html',
  styleUrls: ['./add-billing-address-form.component.scss']
})
export class AddBillingAddressFormComponent implements OnInit {
  addressForm = new UntypedFormGroup({});
  @Input() countries: Array<CountriesListModel> = [];
  @Input() dialogType: string = "";
  @Input() currentAddressDetails: any = "";
  @Output() onSelectedData: EventEmitter<any> = new EventEmitter();
  @ViewChild('hiddenCountrySelect') hiddenCountrySelect?: ElementRef<HTMLSelectElement>;
  invalidPhoneNumber: boolean = false;
  isUpdateBoolean: boolean = false
  addressType: 'individual' | 'business' = 'individual';
  isGuestMode: boolean = false;
  
  // Static autocomplete IDs to prevent ExpressionChangedAfterItHasBeenCheckedError
  autocompleteIds = {
    fullName: '',
    legalName: '',
    companyName: '',
    gstNumber: '',
    houseNumber: '',
    apartmentName: '',
    country: '',
    state: '',
    townCity: '',
    postalCode: '',
    phoneNumber: ''
  };
  constructor(public commonService: CommonService,
  private billingService: BillingAddressService,
  public formService: FormsService,
  public shakeAnimation: ShakeAnimationService,
  private ga4TrackingService: Ga4trackingService,
  public browserStorageService: BrowserStorageService) {}

  ngOnInit(): void {
    this.createForm();
    if (Object.keys(this.currentAddressDetails).length > 0) {
      this.isUpdateBoolean = true;
      this.setAddressFormData();
    }
  }

  /**
   * Initializes Address form
   */
  createForm() {
    this.addressForm = new UntypedFormGroup({
      'addressType': this.formService.getFormControl('', 'individual'),
      'legalName': this.formService.getFormControl(''),
      'country': this.formService.getFormControl(''),
      'state': this.formService.getFormControl(''),
      'townCity': this.formService.getFormControl(''),
      'postalCode': this.formService.getFormControl(''),
      'phoneNumber': this.formService.getFormControl(''),
      'phoneCode': this.formService.getFormControl(''),
    });
    
    // Add email field for guest users only
    this.isGuestMode = !this.browserStorageService.getSessionData();
    if (this.isGuestMode) {
      this.addressForm.addControl('email', this.formService.getFormControl('email'));
    }

    // Add business-only fields conditionally
    if (this.addressType === 'business') {
      this.addBusinessFields();
    }
  }

  /**
   * Add business-specific form controls
   */
  addBusinessFields() {
    this.addressForm.addControl('fullName', this.formService.getFormControl(''));
    this.addressForm.addControl('companyName', this.formService.getFormControl(''));
    this.addressForm.addControl('gstNumber', this.formService.getFormControl('without-validate'));
    this.addressForm.addControl('houseNumber', this.formService.getFormControl(''));
  }

  /**
   * Remove business-specific form controls
   */
  removeBusinessFields() {
    this.addressForm.removeControl('fullName');
    this.addressForm.removeControl('companyName');
    this.addressForm.removeControl('gstNumber');
    this.addressForm.removeControl('houseNumber');
  }

  /**
   * Handle address type change
   */
  onAddressTypeChange(type: 'individual' | 'business') {
    this.addressType = type;
    
    if (type === 'business') {
      // Add business fields first if they don't exist
      if (!this.addressForm.contains('fullName')) {
        this.addBusinessFields();
      }
      // Then reset with all fields including business ones
      this.addressForm.reset({
        addressType: 'business',
        fullName: '',
        companyName: '',
        gstNumber: '',
        legalName: '',
        houseNumber: '',
        country: '',
        state: '',
        townCity: '',
        postalCode: '',
        phoneNumber: '',
        phoneCode: ''
      });
    } else {
      // Remove business fields first
      this.removeBusinessFields();
      // Then reset with only individual fields
      this.addressForm.reset({
        addressType: 'individual',
        legalName: '',
        country: '',
        state: '',
        townCity: '',
        postalCode: '',
        phoneNumber: '',
        phoneCode: ''
      });
    }
    // Reset hidden country select to allow browser autofill to trigger again
    if (this.hiddenCountrySelect?.nativeElement) {
      this.hiddenCountrySelect.nativeElement.value = '';
    }
  }

  setAddressFormData() {
    // Determine address type from existing data. Ignore .name because it's populated for both individual and business
    const hasBusinessData = this.currentAddressDetails.company_name || this.currentAddressDetails.gst_number || (this.currentAddressDetails.address && this.currentAddressDetails.address.line_1);
    this.addressType = hasBusinessData ? 'business' : 'individual';
    
    // Add business fields if needed
    if (this.addressType === 'business') {
      this.addBusinessFields();
    }
    
    // Patch common values
    const patchData: any = {
      'addressType': this.addressType,
      'legalName': this.currentAddressDetails.legal_name ? this.currentAddressDetails.legal_name : '',
      'country': this.currentAddressDetails.country ? this.setDownValue("countryName", this.currentAddressDetails.country) : '',
      'state': this.currentAddressDetails.state ? this.currentAddressDetails.state : '',
      'townCity': this.currentAddressDetails.city ? this.currentAddressDetails.city : '',
      'postalCode': this.currentAddressDetails.pin_code ? this.currentAddressDetails.pin_code : '',
      'phoneNumber': this.currentAddressDetails.phone ? this.currentAddressDetails.phone : '',
      'phoneCode': this.currentAddressDetails.country ? this.setDownValue("countryPhoneCode", this.currentAddressDetails.country) : '',
    };
    
    // Add business-specific values if business type
    if (this.addressType === 'business') {
      patchData['fullName'] = this.currentAddressDetails.name ? this.currentAddressDetails.name : '';
      patchData['companyName'] = this.currentAddressDetails.company_name ? this.currentAddressDetails.company_name : '';
      patchData['gstNumber'] = this.currentAddressDetails.gst_number ? this.currentAddressDetails.gst_number : '';
      patchData['houseNumber'] = this.currentAddressDetails.address?.line_1 ? this.currentAddressDetails.address.line_1 : '';
    }
    
    // Patch email for guest users if previously entered
    if (this.isGuestMode && this.currentAddressDetails.email) {
      patchData['email'] = this.currentAddressDetails.email;
    }
    
    this.addressForm.patchValue(patchData);
    this.selectCountryMobileNumber('');
    
    // Remove phone code from phone number if present
    this.cleanPhoneNumber();
  }
  
  /**
   * Remove phone code prefix from phone number field
   */
  cleanPhoneNumber() {
    const phoneNumber = this.addressForm.controls['phoneNumber'].value;
    if (!phoneNumber) return;
    
    let cleanedNumber = phoneNumber.toString().trim();
    
    // Get all dial codes from countries array
    const allDialCodes = this.countries
      .map(c => c.dialCode)
      .filter(code => code && code.length > 0)
      .sort((a, b) => b.length - a.length); // Sort by length descending to match longest first
    
    // Remove any country code prefix
    for (const dialCode of allDialCodes) {
      if (cleanedNumber.startsWith(dialCode)) {
        cleanedNumber = cleanedNumber.substring(dialCode.length).trim();
        break;
      }
    }
    
    // Update the form control if the number was changed
    if (cleanedNumber !== phoneNumber) {
      this.addressForm.controls['phoneNumber'].setValue(cleanedNumber);
    }
  }

  /**
   * Handing billing address update and insert
   */
  addressEvents() {
    if (this.addressForm.valid) {
      let  formValue: any = this.addressForm.value;
      const sendData: InsertBillingAddressModel = {
        "name":  this.addressType === 'business' ? (formValue.fullName || '') : formValue.legalName,
        "legal_name":formValue.legalName || '',
        "company_name": this.addressType === 'business' ? (formValue.companyName || '') : '',
        "gst_number": this.addressType === 'business' ? (formValue.gstNumber || '') : '',
        "country":this.getDropDownValue("",formValue.country),
        "state":formValue.state,
        "city":formValue.townCity,
        "address": {
          "line_1": this.addressType === 'business' ? (formValue.houseNumber || '') : '',
          "line_2": ''
        },
        "pin_code":formValue.postalCode,
        "phone": this.getDropDownValue("countryDialCode", this.addressForm.controls['phoneCode'].value)  + formValue.phoneNumber     
      }
      // Include email for guest checkout only
      if (this.isGuestMode && formValue.email) {
        sendData.email = formValue.email;
      }
      if (this.isGuestMode) {
        this.commonService.closingPopupWithSendData(sendData);
      } else {
        this.isUpdateBoolean ?  this.updateBillingAddress(sendData) : this.insertBillingAddress(sendData); 
      }
      let eventType = 'add_new_billing_address_completed';
      this.ga4TrackingService.sendEvent(eventType, {
        event_category: 'billing_address_management',
        event_label: "billing_address_form_submission",
        address_type: this.addressType,
        userRole: this.browserStorageService?.getCurrentRoles() || 'guest'
      });
    } else {
      this.addressForm.markAllAsTouched();
    }
  }

  /**
   * In this function insert the new address
   * @param sendData 
   */
  insertBillingAddress(sendData: InsertBillingAddressModel) {
    this.billingService.insertBillingAddress(sendData).subscribe((res: any) => {
      const savedAddress = Array.isArray(res?.data) ? res.data[0] : res?.data;
      this.commonService.closingPopupWithSendData(savedAddress);
    }, (error) => {
      this.commonService.closePopUp();
      this.commonService.showToastError(error)
    });
  }

  /**
   * In this function update the billing address
   * @param sendData 
   */
  updateBillingAddress(sendData: InsertBillingAddressModel) {
    // this.billingService.insertBillingAddress(sendData).subscribe((res: any) => {
      this.commonService.closingPopupWithSendData(this.currentAddressDetails);
    // }, (error) => {
    //   this.commonService.showToastError(error)
    // });
  }

  setDownValue(type: string, value: any) {
    let finalObject: any = ""
      let keyName =  type === 'countryName' ? 'isoCode' : 'dialCode';
      let found = this.countries.find( (item: any) =>  item[keyName] === value);
      if (found) {
        finalObject = found;
      }
    return finalObject;
  }

  getDropDownValue(type: string, value: any) {
    let finalValue = "";
    if (type === "countryDialCode") {
      finalValue =  value?.dialCode;
    } else {
      finalValue =  value?.isoCode;
    }
    return finalValue;
  }

  /**
   * Handle browser autofill for country field
   * @param event - Change event from hidden native select
   */
  onCountryAutofill(event: any) {
    const autofilledCode = event.target.value; // e.g., "IN" from browser

    // Find the full country object by ISO code
    const matchingCountry = this.countries.find(c => c.isoCode === autofilledCode);

    if (matchingCountry) {
      // Set the full object in the form control
      this.addressForm.controls['country'].setValue(matchingCountry);

      // Trigger phone code sync and other logic
      this.selectCountryMobileNumber({ value: matchingCountry });
      
      // Clean phone number after country selection
      this.cleanPhoneNumber();
    }
  }

  selectCountryMobileNumber(event: any) {
    let selectedCountry = this.addressForm.controls['country'].value;
    let found =   this.countries.find( item => item.name === selectedCountry.name);
    if (found) {
      this.addressForm.controls['phoneCode'].setValue(found);
      // Clean phone number after phone code change
      this.cleanPhoneNumber();
      this.validatePhoneNumber();
    }
  }
  selectPhoneCode(event: any) {
    let selectedCode = this.addressForm.controls['phoneCode'].value;
    this.addressForm.controls['phoneNumber'].setValue('');
    this.validatePhoneNumber();
  }
  closePopup() {
    this.commonService.closePopUp();
  }

  // Call this on blur or after autofill
  onPhoneNumberBlur() {
    this.cleanPhoneNumber();
    this.validatePhoneNumber();
  }
  /**
   * Checks if phone number contains invalid characters (anything except digits and + at start)
   */
  validatePhoneNumber() {
    const value = this.addressForm.controls['phoneNumber'].value;
    // Allow + at the start, then only digits
    this.invalidPhoneNumber = value && !/^\+?[0-9]*$/.test(value);
  }

  onChangeFullName(){
    if(this.addressForm.controls['addressType'].value === 'individual') return ; 
    this.addressForm.controls['legalName'].setValue(this.addressForm.controls['fullName'].value.trim());
  }
}
